"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { ToggleLoading } from '../../foundation/loading';
import { handleModalOpen } from '../../elements/modal';
import { smartFetch } from '../../foundation/smartfetch';
export async function processFormData(e, callback, options = {}) {
    const target = e.target;
    const myform = document.getElementById(target.id);
    e.preventDefault?.();
    const myFormData = {};
    const formData = new FormData(myform);
    for (const [key, value] of formData.entries()) {
        myFormData[key] = value;
    }
    if (options.sendQueryParams && typeof window !== 'undefined') {
        for (const [key, value] of new URLSearchParams(window.location.search)) {
            if (!(key in myFormData))
                myFormData[key] = value;
        }
    }
    return processJSON(myFormData, () => callback?.(e));
}
export async function processJSON(jsonData, callback) {
    const debug = false;
    const sendmail_api = "https://sendmail.pixelated.tech/default/sendmail";
    // "https://admin.pixelated.tech/api/process-form-submit"
    const myJsonData = {};
    for (const [key, value] of Object.entries(jsonData)) {
        myJsonData[key] = value;
    }
    // Check the canonical honeypot keys plus the legacy website field convention.
    if (String(myJsonData['winnie'] ?? '').trim() ||
        String(myJsonData['pooh'] ?? '').trim() ||
        String(myJsonData['website'] ?? '').trim()) {
        if (debug)
            console.info('honeypot triggered — dropping submit');
        callback?.();
        return { success: true, response: null };
    }
    myJsonData.Date = new Date().toLocaleDateString();
    myJsonData.Status = "Submitted";
    const startTime = new Date().toISOString();
    if (debug)
        console.info('[processJSON] submit-start', { sendmail_api, startTime, myJsonData });
    try {
        const responseData = await smartFetch(sendmail_api, {
            requestInit: {
                method: 'POST',
                mode: 'cors',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(myJsonData),
            }
        });
        const elapsedMs = new Date().getTime() - new Date(startTime).getTime();
        if (debug)
            console.info('[processJSON] submit-finish', { sendmail_api, elapsedMs, responseData });
        if (debug)
            console.debug('processJSON — submission data:', myJsonData, 'response:', responseData);
        callback?.();
        return { success: true, response: responseData };
    }
    catch (err) {
        console.error('processJSON error', err);
        callback?.();
        return { success: false, error: err };
    }
}
const DEFAULT_MODAL_CONTENT = (_jsxs("div", { className: "centered", children: [_jsx("br", {}), _jsx("br", {}), "Thank you for your submission!", _jsx("br", {}), "We will get back to you as soon as we can.", _jsx("br", {}), _jsx("br", {}), _jsx("br", {})] }));
export function useFormSubmit(options = {}) {
    const { toggleLoading = true, openModal = true, resetForm = true, sendQueryParams = false, modalContent = DEFAULT_MODAL_CONTENT, onStart, onSuccess, onError, onFinally, } = options;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitResponse, setSubmitResponse] = useState(null);
    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        const nativeEvent = event.nativeEvent;
        // Start
        if (toggleLoading)
            ToggleLoading({ show: true });
        onStart?.();
        setIsSubmitting(true);
        setSubmitError(null);
        // Submit
        const result = await processFormData(nativeEvent, undefined, { sendQueryParams });
        setSubmitResponse(result.response);
        // Handle result
        if (result.success) {
            // Run defaults first
            if (openModal) {
                // Use the Modal context if available, or try handleModalOpen
                try {
                    handleModalOpen(nativeEvent);
                }
                catch (err) {
                    // Modal system not available in test/story context
                    if (typeof window !== 'undefined' && window.__DEV__) {
                        console.debug('handleModalOpen not available', err);
                    }
                }
            }
            if (resetForm) {
                const form = nativeEvent.target;
                if (form)
                    form.reset();
            }
            // Run custom callback
            onSuccess?.(nativeEvent, result.response);
        }
        else {
            const error = result.error ?? new Error('Form submission failed');
            setSubmitError(error);
            // Run custom callback
            onError?.(nativeEvent, error);
        }
        // Finalize
        onFinally?.(nativeEvent);
        if (toggleLoading)
            ToggleLoading({ show: false });
        setIsSubmitting(false);
    }, [toggleLoading, openModal, resetForm, onStart, onSuccess, onError, onFinally]);
    return {
        isSubmitting,
        submitError,
        submitResponse,
        handleSubmit,
        modalContent: modalContent,
    };
}
